import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Loader from "Components/UI/Loader";
import { getLocalStorage } from "Network/ApiService";
import { ls_vars } from "Library/Constants";
import MaterialSelect from "@mui/material/Select";

import Label from "Components/UI/Label";
// Images
import images from "Assets/images";
// css
import "Components/Common/Modal/style.css";
import "Components/Core/BookingFormModule/new-booking-form.css";
import { useApiCall } from "Hooks/useApiCall";
import { MenuItem } from "@mui/material";
import {
  createEquipment,
  updateEquipment,
} from "Network/Core/EquipmentMaster/equipment";
import { getFilteredCommonUtils } from "Network/Core/CommonUtils/commonUtils";
import { EquipmentType } from "Network/Core/EquipmentMaster/types";
import React from "react";
import FileUpload from "Components/UI/FileUpload";
import { useTranslationCheck } from "Hooks/useTranslationCheck";

export interface EquipmentMasterModalProps {
  handleClose?: any;
  existingData: EquipmentType;
  categoryOptions: any;
  fuelTypeOptions: any;
}

const EquipmentMasterModal = ({
  handleClose,
  existingData,
  categoryOptions,
  fuelTypeOptions,
}: EquipmentMasterModalProps) => {
  const project = getLocalStorage(ls_vars.PROJECT_DATA);
  const site = getLocalStorage(ls_vars.PROJECT_SITE_ID);
  const region = getLocalStorage(ls_vars.REGION_ID);
  const { t, tCheck } = useTranslationCheck();
  const { call } = useApiCall();
  const [equipment, setEquipment] = useState<EquipmentType>({
    id: 0,
    name: "",
    category: "",
    brand: "",
    model: "",
    fuelType: "",
    emissionFactor: 0.0,
    tempEmissionFactor: 0.0, // Add any other fields here with default values
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isUploadFileVisible, setIsUploadFileVisible] = useState<boolean>(
    existingData?.id ? false : true
  );
  const [validationErrors, setValidationErrors] = useState<{
    emissionFactor?: string;
  }>({});

  const handleChange = (e: { target: { name: string; value: any } }) => {
    const { name, value } = e.target;
    setIsUploadFileVisible(false);

    let processedValue: string | number;
    switch (name) {
      case "emissionFactor":
        processedValue = value || "";
        break;
      default:
        processedValue = value || "";
    }

    // Always update the state to allow normal text editing
    setEquipment((prev: EquipmentType) => ({
      ...prev,
      [name]: processedValue,
    }));

    // Clear previous validation error for this field
    setValidationErrors((prev) => ({
      ...prev,
      [name]: undefined,
    }));

    // Validate emission factor after updating state
    if (name === "emissionFactor") {
      const numValue = parseFloat(value);
      if (value !== "" && !isNaN(numValue) && numValue <= 0) {
        setValidationErrors((prev) => ({
          ...prev,
          [name]: t("Translation.value_must_be_greater_than_zero"),
        }));
      }
    }
  };

  useEffect(() => {
    if (existingData && existingData.id) {
      setEquipment(existingData);
    }
  }, [existingData]);

  const handleCreateEquipmentSubmit = async (e: any) => {
    e.preventDefault();
    
    // Validate all numeric fields before submission
    const errors: typeof validationErrors = {};
    
    if (equipment.emissionFactor <= 0) {
      errors.emissionFactor = t("Translation.value_must_be_greater_than_zero");
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return; // Stop submission if there are validation errors
    }
    
    call(
      () => createEquipment(equipment, region, project.ref, site),
      (data) => {
        setIsLoading(false);
        handleClose();
      },
      { successMessage: "Translation.equipment_master_add_success" }
    );
    // setIsLoading(false);
  };

  const handleUpdateEquipmentSubmit = async (e: any) => {
    e.preventDefault();
    if (!existingData || existingData.id === undefined) {
      console.error("Cannot update equipment: Invalid ID");
      return;
    }

    // Validate all numeric fields before submission
    const errors: typeof validationErrors = {};
    
    if (equipment.emissionFactor <= 0) {
      errors.emissionFactor = t("Translation.value_must_be_greater_than_zero");
    }
    
    if (Object.keys(errors).length > 0) {
      setValidationErrors(errors);
      return; // Stop submission if there are validation errors
    }

    const id = existingData.id;
    call(
      () => updateEquipment(id, equipment, region, project.ref, site),
      (data) => {
        setIsLoading(false);
        handleClose();
      },
      { successMessage: "Translation.equipment_master_edit_success" }
    );
    // setIsLoading(false);
  };
  return (
    <>
      {isLoading && <Loader />}
      <div
        className="modal fade  show vc-user-invite"
        id="invite_user_Modal"
        style={{ display: "block" }}
      >
        <div className="modal-dialog modal-dialog-scrollable ">
          <div className="modal-content">
            <div className="modal-header">
              <div className="invite-user-header">
                <h2 className="text-left">
                  {t(
                    existingData && existingData.id
                      ? "Translation.equipment_master_edit"
                      : "Translation.equipment_master_add"
                  )}
                </h2>
              </div>
              <div className="invite-user-header vc-invite-from-procore-link">
                <Link onClick={handleClose} to="">
                  {t("Translation.close")}
                  <img src={images.modalCloseIcon} alt="" />
                </Link>
              </div>
            </div>
            <div className="modal-body">
              <div className="custom-form ">
                <form
                  onSubmit={
                    existingData && existingData.id
                      ? handleUpdateEquipmentSubmit
                      : handleCreateEquipmentSubmit
                  }
                >
                  <div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <div className="row ml-1 align-items-center">
                          <Label className="mr-1" label={t("Translation.equipment_name")} />
                          <span className="text-danger fs-1">*</span>
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <input
                          type="text"
                          className="form-control"
                          id="equipmentName"
                          name="name"
                          value={equipment.name}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <div className="row ml-1 align-items-center">
                          <Label className="mr-1" label={t("Translation.equipment_category")} />
                          <span className="text-danger fs-1">*</span>
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <MaterialSelect
                          style={{
                            width: "22rem",
                          }}
                          inputProps={{
                            className: "form-control",
                            id: "equipmentCategory",
                            name: "category",
                            required: true,
                          }}
                          sx={{
                            width: "100%",
                            height: "48px",
                            background: "#3EFF3F6",
                            color: "#A0AEBD",
                            border: "none",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          }}
                          value={equipment.category || ""}
                          onChange={(e: any) => handleChange({ target: { name: "category", value: e.target.value } })}
                        >
                          {categoryOptions.map((item: any) => (
                            <MenuItem value={item.value} key={item.value}>
                              {tCheck(item.label)}
                            </MenuItem>
                          ))}
                        </MaterialSelect>
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <div className="row ml-1 align-items-center">
                          <Label className="mr-1" label={t("Translation.equipment_brand")} />
                          <span className="text-danger fs-1">*</span>
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <input
                          type="text"
                          className="form-control"
                          id="equipmentBrand"
                          name="brand"
                          value={equipment.brand}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <div className="row ml-1 align-items-center">
                          <Label className="mr-1" label={t("Translation.equipment_model")} />
                          <span className="text-danger fs-1">*</span>
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <input
                          type="text"
                          className="form-control"
                          id="equipmentModel"
                          name="model"
                          value={equipment.model}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <div className="row ml-1 align-items-center">
                          <Label className="mr-1" label={t("Translation.fuel_type")} />
                          <span className="text-danger fs-1">*</span>
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <MaterialSelect
                          style={{
                            width: "22rem",
                          }}
                          inputProps={{
                            className: "form-control",
                            id: "fuelType",
                            name: "fuelType",
                            required: true,
                          }}
                          sx={{
                            width: "100%",
                            height: "48px",
                            background: "#3EFF3F6",
                            color: "#A0AEBD",
                            border: "none",
                            "& .MuiOutlinedInput-notchedOutline": {
                              border: "none",
                            },
                          }}
                          value={equipment.fuelType || ""}
                          onChange={(e: any) => handleChange({ target: { name: "fuelType", value: e.target.value } })}
                          required
                        >
                          {fuelTypeOptions.map((item: any) => (
                            <MenuItem value={item.value} key={item.value}>
                              {tCheck(item.label)}
                            </MenuItem>
                          ))}
                        </MaterialSelect>
                      </div>
                    </div>
                    <div className="row mb-3 align-items-center">
                      <div className="col-sm-4">
                        <div className="row ml-1 align-items-center">
                          <Label className="mr-1" label={t("Translation.emission_factor")} />
                          <span className="text-danger fs-1">*</span>
                        </div>
                      </div>
                      <div className="col-sm-8">
                        <input
                          type="number"
                          step="0.01" // This allows for 2 decimal places
                          className={`form-control ${validationErrors.emissionFactor ? "is-invalid" : ""}`}
                          id="emissionFactor"
                          name="emissionFactor"
                          value={equipment.emissionFactor}
                          onChange={handleChange}
                          required
                        />
                        {validationErrors.emissionFactor && (
                          <div className="invalid-feedback" style={{ display: "block" }}>
                            {validationErrors.emissionFactor}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="modal-footer">
                      <button type="submit" className="btn theme-btn">                        
                        {t("Translation.submit")}
                      </button>
                    </div>
                  </div>
                </form>
                <div>
                  {isUploadFileVisible && (
                    <FileUpload
                      project={project.ref}
                      region={region}
                      type="equipment"
                      handleClose={() => {
                        setIsUploadFileVisible(false);
                      }}
                      onUploadSuccess={() => {
                        setIsUploadFileVisible(false);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EquipmentMasterModal;
